import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../app/providers/AuthProvider";
import { getAuthGeneration } from "../../api/axiosClient";
import ProductGuidanceContext from "./ProductGuidanceContext";
import { createDriverTourEngine } from "./engine/driverTourEngine";
import { getTourDefinition, TOUR_REGISTRY } from "./tourRegistry";
import {
  getProductGuidanceStates,
  consumeProductGuidanceForceReplay,
  updateProductGuidanceState,
} from "./productGuidanceApi";
import "./productGuidance.css";

export default function ProductGuidanceProvider(props) {
  const { isLoading, businessId, isPlatformRole, user } = useAuth();
  // Remount the state owner at an identity boundary so a previous scope's
  // cache, observers, and in-flight responses cannot be reused by the next one.
  const scopeKey = JSON.stringify([
    getAuthGeneration(),
    user?.id ?? user?.Id ?? null,
    businessId ?? null,
    !!isPlatformRole,
  ]);

  return (
    <ScopedProductGuidanceProvider
      {...props}
      key={scopeKey}
      canPersistGuidance={!isLoading && !!businessId && !isPlatformRole}
    />
  );
}

function ScopedProductGuidanceProvider({
  children,
  registry = TOUR_REGISTRY,
  createEngine = createDriverTourEngine,
  canPersistGuidance,
}) {
  const location = useLocation();
  // Guidance state belongs to the business user in the token, not a platform
  // admin's selected business. Platform tours can still run locally.
  const activeEngineRef = useRef(null);
  const forcedStartsRef = useRef(new Set());
  const mountedRef = useRef(true);
  const [statesByIdentity, setStatesByIdentity] = useState({});

  const stateIdentity = useCallback(
    definition =>
      `${definition.experienceType}:${definition.experienceKey}:${definition.version}`,
    []
  );

  const rememberState = useCallback(state => {
    if (!state?.experienceKey || !mountedRef.current) return;
    const identity = `${state.experienceType}:${state.experienceKey}:${state.version}`;
    setStatesByIdentity(current => ({ ...current, [identity]: state }));
  }, []);

  const persistState = useCallback(
    (definition, status, currentStep) => {
      if (
        !mountedRef.current ||
        !canPersistGuidance ||
        !definition?.experienceType ||
        !definition?.experienceKey ||
        !definition?.version
      ) {
        return;
      }

      updateProductGuidanceState({
        experienceType: definition.experienceType,
        experienceKey: definition.experienceKey,
        version: definition.version,
        status,
        currentStep,
      })
        .then(rememberState)
        .catch(() => {});
    },
    [canPersistGuidance, rememberState]
  );

  const stopTour = useCallback(() => {
    const activeTour = activeEngineRef.current;
    activeEngineRef.current = null;

    if (!activeTour) {
      return false;
    }

    try {
      activeTour.engine.stop();
      return true;
    } catch {
      return false;
    }
  }, []);

  const startTour = useCallback(
    (tourKey, { forceReplay = false } = {}) => {
      if (!mountedRef.current) return false;
      const definition = getTourDefinition(tourKey, registry);

      if (!definition) {
        return false;
      }

      const steps = definition.steps.filter(step => {
        if (typeof step.element !== "string") return true;
        try {
          return Boolean(document.querySelector(step.element));
        } catch {
          return false;
        }
      });

      if (steps.length === 0) {
        return false;
      }

      stopTour();

      try {
        const activeTour = { engine: null, currentStep: 0, definition };
        const engine = createEngine({
          steps,
          options: definition.options,
          onStepChange: currentStep => {
            activeTour.currentStep = currentStep;
            persistState(definition, "InProgress", currentStep);
          },
          onCompleted: () =>
            persistState(definition, "Completed", activeTour.currentStep),
          onDismissed: () =>
            persistState(definition, "Dismissed", activeTour.currentStep),
        });

        activeTour.engine = engine;
        activeEngineRef.current = activeTour;
        engine.start();
        if (forceReplay && canPersistGuidance) {
          consumeProductGuidanceForceReplay({
            experienceType: definition.experienceType,
            experienceKey: definition.experienceKey,
            version: definition.version,
          })
            .then(rememberState)
            .catch(() => {});
        } else {
          persistState(definition, "InProgress", 0);
        }
        return true;
      } catch {
        stopTour();
        return false;
      }
    },
    [canPersistGuidance, createEngine, persistState, registry, rememberState, stopTour]
  );

  const isTourActive = useCallback(() => {
    try {
      return activeEngineRef.current?.engine.isActive() ?? false;
    } catch {
      return false;
    }
  }, []);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      // Fence old callbacks before DOM observers or asynchronous work can run.
      mountedRef.current = false;
      stopTour();
    };
  }, [stopTour]);

  useEffect(() => {
    let cancelled = false;
    if (canPersistGuidance) {
      getProductGuidanceStates()
        .then(states => {
          if (cancelled || !mountedRef.current) return;
          setStatesByIdentity(current => {
            const loaded = {};
            states.forEach(state => {
              if (!state?.experienceKey) return;
              loaded[`${state.experienceType}:${state.experienceKey}:${state.version}`] = state;
            });
            return { ...loaded, ...current };
          });
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [canPersistGuidance]);

  useEffect(() => {
    if (!canPersistGuidance || !mountedRef.current) return undefined;
    const routeMatches = definition =>
      definition.forceReplayRoutes?.some(route =>
        route.endsWith("/*")
          ? location.pathname.startsWith(route.slice(0, -1))
          : location.pathname === route
      );
    const definition = Object.values(registry).find(candidate => {
      const state = statesByIdentity[stateIdentity(candidate)];
      return state?.forceReplayPending === true && routeMatches(candidate);
    });

    if (!definition || isTourActive()) return undefined;

    const identity = stateIdentity(definition);
    if (forcedStartsRef.current.has(identity)) return undefined;

    let observer;
    const attemptStart = () => {
      if (forcedStartsRef.current.has(identity) || isTourActive()) return false;
      const started = startTour(definition.experienceKey, { forceReplay: true });
      if (started) {
        forcedStartsRef.current.add(identity);
        observer?.disconnect();
      }
      return started;
    };

    if (!attemptStart() && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(attemptStart);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => observer?.disconnect();
  }, [
    canPersistGuidance,
    isTourActive,
    location.pathname,
    registry,
    startTour,
    stateIdentity,
    statesByIdentity,
  ]);

  const getTourState = useCallback(
    tourKey => {
      const definition = getTourDefinition(tourKey, registry);
      return definition
        ? statesByIdentity[stateIdentity(definition)] ?? null
        : null;
    },
    [registry, stateIdentity, statesByIdentity]
  );

  const value = useMemo(
    () => ({ startTour, stopTour, isTourActive, getTourState }),
    [getTourState, isTourActive, startTour, stopTour]
  );

  return (
    <ProductGuidanceContext.Provider value={value}>
      {children}
    </ProductGuidanceContext.Provider>
  );
}
